import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, AutocompleteRenderGetTagProps, Box, Checkbox, Chip, FormControlLabel, MenuItem, 
  Select, SelectChangeEvent, Stack, TextField, Typography } from "@mui/material";
import { useGetMainList } from "ra-core";
import { FC, useMemo, useState } from "react";
import { RecPatch } from "@/dataProvider/tools";
import { CreationAssigment, DataAccessRoleResponseDto } from "@/gen/api/governance";
import { pages, sorts } from "@/utils/paramsWithGetMainList";


const darRes = "settings/governance/data-access-role"
type AssignType = 'explicit'|'except'|'toAll'

const ClassificationDARAssignment: FC<{setDarAssignment: (x: CreationAssigment|null)=>void}> = ({
  setDarAssignment
}) => {
  const { data, ids } = useGetMainList<DataAccessRoleResponseDto&RecPatch>(darRes, pages(), sorts(), {})
  const darOptions = useMemo(() => {
    return (ids ?? []).map(id => {
      const x = data![id]
      return x
    }).sort((a, b) => a.name!.toLowerCase() < b.name!.toLowerCase() ? -1 : 1)
  }, [data, ids])

  const [states, setStates] = useState({
    enabled: false,
    read: { 
      at: 'explicit' as AssignType,
      expanded: false,
      ls: [] as DataAccessRoleResponseDto[],
    },
    write: { 
      at: 'explicit' as AssignType,
      expanded: false,
      ls: [] as DataAccessRoleResponseDto[],
    },
  })

  const collectAssignment = (v1: typeof states): CreationAssigment|null => {
    if (!v1.enabled) { return null }
    if (v1.write.at === 'toAll') {
      return {
        addWriteToAll: true,
        addReadToAll: true,
      }
    }
    if (v1.read.at === 'toAll') {
      return { addReadToAll: true, ...Object.fromEntries([
        [v1.write.at === 'explicit' ? 'addWriteExplicit' : 'addWriteExcept', v1.write.ls.map(x => x.roleId!)]
      ])}
    }
    if (v1.read.ls.length === 0 && v1.write.ls.length === 0) { return null }
    return Object.fromEntries([
      [v1.read.at === 'explicit' ? 'addReadExplicit' : 'addReadExcept', v1.read.ls.map(x => x.roleId!)],
      [v1.write.at === 'explicit' ? 'addWriteExplicit' : 'addWriteExcept', v1.write.ls.map(x => x.roleId!)]
    ])
  }
  const reconcile = (v1: typeof states, baseOnRead: boolean): typeof states => {
    if (v1.read.at === 'toAll') { return v1 }
    if (v1.write.at === 'toAll') {
      if (!baseOnRead) {
        v1.read.at = 'toAll'
        v1.read.ls = []
        return {...v1}
      } else {
        v1.write.at = v1.read.at
        v1.write.ls = [...v1.read.ls]
        return {...v1}
      }
    }
    const ss = [new Set(v1.write.ls.map(x => x.roleId!)), new Set(v1.read.ls.map(x => x.roleId!))] as const
    const _1 = new Set(darOptions.map(x => x.roleId!))
    
// lsW = U + dw
// lsR = U + dr
// 1 = U + dw + dr + Q
//
// lsW_ = 1 - lsW = dr + Q
// lsR_ = 1 - lsR = dw + Q
//
// define 2 functions: diff, intersect, so that:
//   diff(lsW, lsR) = dw
//   intersect(lsW, lsR) = U
// let (-) = diff
// let (@) = intersect
//
// U = lsW @ lsR     = 1 - lsW_ - lsR_ = lsW - (lsW @ lsR_)  = lsR - (lsR @ lsW_)
// Q = 1 - lsW - lsR = lsW_ @ lsR_     = lsR_ - (lsW @ lsR_) = lsW_ - (lsR @ lsW_)
//
// if baseOnRead then
//   lsW' = U
//   lsW_' = dr + dw + Q = 1 - U
// else
//   lsR' = U + dr + dw = 1 - Q
//   lsR_' = Q
//
// To sum this up:
// match (W, R, baseOnRead) with
// | (lsW, lsR, true)    -> lsW'  = lsW @ lsR
// | (lsW, lsR, false)   -> lsR'  = 1 - (1 - lsW - lsR)
// | (lsW_, lsR_, true)  -> lsW_' = 1 - (1 - lsW_ - lsR_)
// | (lsW_, lsR_, false) -> lsR_' = lsW_ @ lsR_
// | (lsW_, lsR, true)   -> lsW_' = 1 - (lsR - (lsR @ lsW_))
// | (lsW_, lsR, false)  -> lsR'  = 1 - (lsW_ - (lsR @ lsW_))
// | (lsW, lsR_, true)   -> lsW'  = lsW - (lsW @ lsR_)
// | (lsW, lsR_, false)  -> lsR_' = lsR_ - (lsW @ lsR_)

    const sig = 
      (v1.write.at === 'explicit' ? 0 : 1) + ':' + 
      (v1.read.at === 'explicit' ? 0 : 1) + ':' + 
      (baseOnRead ? 0 : 1)
    switch (sig) {
      case '0:0:0': { 
        const [lsW, lsR] = ss
        const U = lsW.intersection(lsR)
        v1.write.ls = v1.write.ls.filter(x => U.has(x.roleId!))
        break; 
      }
      case '0:0:1': { 
        const [lsW, lsR] = ss
        const Q = _1.difference(lsW).difference(lsR)
        // if (Q.size > (_1.size / 2)) {
          const d = _1.difference(Q)
          v1.read.ls = darOptions.filter(x => d.has(x.roleId!))
        // } else {
        //   v1.read.at = 'except'
        //   v1.read.ls = darOptions.filter(x => Q.has(x.roleId!))
        // }
        break; 
      }
      case '1:1:0': { 
        const [lsW_, lsR_] = ss
        const U = _1.difference(lsW_).difference(lsR_)
        // if (U.size > (_1.size / 2)) {
          const d = _1.difference(U)
          v1.write.ls = darOptions.filter(x => d.has(x.roleId!))
        // } else {
        //   v1.write.at = 'explicit'
        //   v1.write.ls = darOptions.filter(x => U.has(x.roleId!))
        // }
        break; 
      }
      case '1:1:1': { 
        const [lsW_, lsR_] = ss
        const Q = lsW_.intersection(lsR_)
        v1.read.ls = v1.read.ls.filter(x => Q.has(x.roleId!))
        break; 
      }
      case '1:0:0': { 
        const [lsW_, lsR] = ss
        const U = lsR.difference(lsR.intersection(lsW_))
        // if (U.size > (_1.size / 2)) {
          const d = _1.difference(U)
          v1.write.ls = darOptions.filter(x => d.has(x.roleId!))
        // } else {
        //   v1.write.at = 'explicit'
        //   v1.write.ls = darOptions.filter(x => U.has(x.roleId!))
        // }
        break; 
      }
      case '1:0:1': { 
        const [lsW_, lsR] = ss
        const Q = lsW_.difference(lsR.intersection(lsW_))
        // if (Q.size > (_1.size / 2)) {
          const d = _1.difference(Q)
          v1.read.ls = darOptions.filter(x => d.has(x.roleId!))
        // } else {
        //   v1.read.at = 'except'
        //   v1.read.ls = darOptions.filter(x => Q.has(x.roleId!))
        // }
        break; 
      }
      case '0:1:0': { 
        const [lsW, lsR_] = ss
        const U = lsW.difference(lsW.intersection(lsR_))
        v1.write.ls = v1.write.ls.filter(x => U.has(x.roleId!))
        break; 
      }
      case '0:1:1': { 
        const [lsW, lsR_] = ss
        const Q = lsR_.difference(lsW.intersection(lsR_))
        v1.read.ls = v1.read.ls.filter(x => Q.has(x.roleId!))
        break; 
      }
    }

    return {...v1}
  }
  const handleATChange = (s: 'read'|'write') => (e: SelectChangeEvent<"explicit" | "except" | "toAll">) => {
    setStates(v => {
      let v1 = {...v}
      const bag = v1[s]
      bag.at = e.target.value as any
      bag.ls = []
      const baseOnRead = (bag.at === 'toAll') !== (s !== 'read')
      v1 = reconcile(v1, baseOnRead)
      setTimeout(() => setDarAssignment(collectAssignment(v1)))
      return v1  
    })
  }
  const handleLsChange = (s: 'read'|'write') => (event: React.SyntheticEvent, value: DataAccessRoleResponseDto[]) => {
    setStates(v => {
      let v1 = {...v}
      const bag = v1[s]
      bag.ls = value
      v1 = reconcile(v1, s === 'read')
      setTimeout(() => setDarAssignment(collectAssignment(v1)))
      return v1  
    })
  }

  const renderTags = (value: DataAccessRoleResponseDto[], getTagProps: AutocompleteRenderGetTagProps) => {
    return value.map((x, index) => (
      <Chip 
        label={x.name ?? ''}
        {...getTagProps({ index })}
      />
    ))
  }
  
  return (
    <Box>
      <FormControlLabel label="Assign this Classification to existing Data Access Roles"
        control={<Checkbox checked={states.enabled} onChange={(e, checked) => setStates(v => ({...v, enabled: checked}))} />}
      />
      {states.enabled &&
        <Box sx={{
          '& div.MuiAccordionDetails-root div.MuiOutlinedInput-root': {
            '& > div.MuiChip-root': {
              height: 'auto', minHeight: 32,
              '& > span.MuiChip-label': {
                display: 'block',
                wordBreak: 'break-all',
                whiteSpace: 'break-spaces',
                color: '#ad5324',
              },
            },
            '& > input': { flex: '100%' },
          },
        }}>
          <Accordion square expanded={states.write.expanded} onChange={(e, expanded) => setStates(v => ({...v, write: {...v.write, expanded}}))}>
            <AccordionSummary aria-controls="panel-write-assignment-content"
              expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
            >
              <Stack direction="column">
                <Typography>Assign <Typography fontWeight="bold" component="span">write</Typography> access to:</Typography>
                {!states.write.expanded && 
                  <Box>
                    <Typography variant="body2" component="span" marginLeft={2} fontWeight={200}>{assignActions[states.write.at]}</Typography>
                    {states.write.at !== 'toAll' && 
                      <Typography variant="body2" component="span" marginLeft={1} color={states.write.at === 'explicit' ? 'success.main' : 'error.main'}>+ {states.write.ls.length}</Typography>
                    }
                  </Box>}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ mt: -1 }}>
              <Select size="small" sx={{width:320, mb: 1}} value={states.write.at} onChange={handleATChange('write')}>
                {Object.entries(assignActions).map(kv =>
                  <MenuItem key={kv[0]} value={kv[0]}>{kv[1]}</MenuItem>
                )}
              </Select>
              {states.write.at !== 'toAll' &&
                <Autocomplete multiple renderTags={renderTags}
                  options={darOptions} 
                  getOptionLabel={(x) => x.name!}
                  renderInput={(params) =>
                    <TextField {...params} placeholder="Select Data Access Role, press Enter to confirm..." />
                  }
                  value={states.write.ls}
                  onChange={handleLsChange('write')}
                />}
            </AccordionDetails>
          </Accordion>
          <Accordion square expanded={states.read.expanded} onChange={(e, expanded) => setStates(v => ({...v, read: {...v.read, expanded}}))}>
            <AccordionSummary aria-controls="panel-read-assignment-content"
              expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
            >
              <Stack direction="column">
                <Typography>Assign <Typography fontWeight="bold" component="span">read</Typography> access to:</Typography>
                {!states.read.expanded && 
                  <Box>
                    <Typography variant="body2" component="span" marginLeft={2} fontWeight={200}>{assignActions[states.read.at]}</Typography>
                    {states.read.at !== 'toAll' && 
                      <Typography variant="body2" component="span" marginLeft={1} color={states.read.at === 'explicit' ? 'success.main' : 'error.main'}>+ {states.read.ls.length}</Typography>
                    }
                  </Box>}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ mt: -1 }}>
              <Select size="small" sx={{width:320, mb: 1}} value={states.read.at} onChange={handleATChange('read')}>
                {Object.entries(assignActions).map(kv =>
                  <MenuItem key={kv[0]} value={kv[0]}>{kv[1]}</MenuItem>
                )}
              </Select>
              {states.read.at !== 'toAll' &&
                <Autocomplete multiple renderTags={renderTags}
                  options={darOptions} 
                  getOptionLabel={(x) => x.name!}
                  renderInput={(params) =>
                    <TextField {...params} placeholder="Select Data Access Role, press Enter to confirm..." />
                  }
                  value={states.read.ls}
                  onChange={handleLsChange('read')}
                />}
            </AccordionDetails>
          </Accordion>
        </Box>}
    </Box>
  )
}

export default ClassificationDARAssignment;


const assignActions : Record<AssignType, string> = {
  explicit: 'Specified Data Access Roles',
  except: 'Data Access Roles except the specified',
  toAll: 'All Data Access Roles',
}
